import React, { useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { OpenInNew } from '@mui/icons-material'
import { Box, DialogContent, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material'

import GRDialogTitle from '../../../../components/GRDialogTitle/GRDialogTitle'
import { GRModal } from '../../../../components/GRModal/GRModal'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { formatDate } from '../../../../helpers/date'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { LockedGameIndicator } from '../../../live-events/components/LockedGameIndicator/LockedGameIndicator'
import { useLockedGameDialog } from '../../../live-events/hooks/useLockedGameDialog'
import { BlueprintDetailResult, useBlueprintDetails } from '../../hooks/useBlueprintDetails'
import type { LevelStatsKind } from '../LevelStatsCard/LevelStatsCard'
import { NewAdopterGameIcon } from '../NewAdopterGameIcon/NewAdopterGameIcon'
import { buildLiveEventsBlueprintModalUrl, buildLiveEventsCalendarUrl } from '../helpers/calendarLinks'
import { CatalogueWindow, catalogueWindowMonths } from '../helpers/catalogueUtils'
import { GAME_ICON_GAP, GAME_ICON_RADIUS, GAME_ICON_SIZE } from '../helpers/drilldownTableLayout'
import { NewAdopterGame, firstAdoptionEvent } from '../helpers/newAdopterGames'

export interface NewAdoptersModalProps {
  open: boolean
  onClose: () => void
  games: NewAdopterGame[]
  // Which level the games are new adopters of; only the empty-state wording differs. Defaults to a blueprint family.
  kind?: LevelStatsKind
  // The blueprint family or event type or category the games adopted, as named in the subtitle.
  entityName: string
  // The list is being (re)loaded, e.g. after the window changed: placeholders show instead of a stale or empty list.
  isLoading?: boolean
  // Only the blueprint-family level resolves each game's first event to deep-link into the calendar; the
  // event type and category levels have no per-game blueprint to resolve, so their links open the game's calendar.
  blueprintFamilyId?: string
  // The family's event type, which addresses the calendar's event modal.
  eventTypeId?: string
  window: CatalogueWindow
}

const SUBTITLE_COPY: Record<LevelStatsKind, string> = {
  blueprint: 'market-analytics:new_adopters_modal_subtitle',
  eventType: 'market-analytics:new_adopters_modal_subtitle_event_type',
  eventCategory: 'market-analytics:new_adopters_modal_subtitle_event_category',
}

const EMPTY_COPY: Record<LevelStatsKind, string> = {
  blueprint: 'market-analytics:new_adopters_modal_empty',
  eventType: 'market-analytics:new_adopters_modal_empty_event_type',
  eventCategory: 'market-analytics:new_adopters_modal_empty_event_category',
}

type CalendarLink = { state: 'pending' } | { state: 'event'; to: string } | { state: 'calendar'; to: string }

const calendarLinkFor = (
  game: NewAdopterGame,
  eventTypeId: string | undefined,
  detail: BlueprintDetailResult | undefined,
  // Where the calendar opens: type and category rows have no event to open, so they land on the game's first event.
  focusOnFirstEvent: boolean
): CalendarLink => {
  const calendar: CalendarLink = {
    state: 'calendar',
    to: buildLiveEventsCalendarUrl({ gameId: game.gameId, focusDate: focusOnFirstEvent ? game.firstEvent : undefined }),
  }
  if (!eventTypeId || !game.firstBlueprintId) return calendar
  if (detail?.isLoading) return { state: 'pending' }
  const event = firstAdoptionEvent(detail?.data?.liveEvents, game.firstEvent)
  return event ? { state: 'event', to: buildLiveEventsBlueprintModalUrl({ gameId: game.gameId, eventTypeId, eventId: event.eventId }) } : calendar
}

/**
 * Every game behind the New Adopters tile, newest adopter first, each linking to the
 * blueprint view of the event that made it one — or to its Live Events calendar when that event can't be named.
 */
export const NewAdoptersModal: React.FC<NewAdoptersModalProps> = ({
  open,
  onClose,
  games,
  kind = 'blueprint',
  entityName,
  isLoading = false,
  blueprintFamilyId,
  eventTypeId,
  window: windowValue,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const titleId = useId()
  const months = catalogueWindowMonths(windowValue)

  const { openLockedGameDialog, lockedGameDialog } = useLockedGameDialog()

  const sortedGames = useMemo(() => [...games].sort((a, b) => b.firstEvent - a.firstEvent), [games])

  // Locked games link to the unlock dialog, so their adoption event is never looked up.
  const firstBlueprintIds = useMemo(
    () => Array.from(new Set(games.flatMap((game) => (game.firstBlueprintId && !game.locked ? [game.firstBlueprintId] : [])))),
    [games]
  )
  const details = useBlueprintDetails(blueprintFamilyId ?? '', firstBlueprintIds, open && Boolean(blueprintFamilyId) && Boolean(eventTypeId))
  const detailByBlueprintId = useMemo(() => new Map(details.map((detail) => [detail.blueprintId, detail])), [details])

  return (
    <GRModal open={open} onClose={onClose} size="small" ariaLabelledby={titleId}>
      <GRDialogTitle onClose={onClose}>
        <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
          <Typography id={titleId} component="span" variant="h3" sx={{ display: 'block' }}>
            {t('market-analytics:new_adopters_modal_title')}
          </Typography>
          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t(SUBTITLE_COPY[kind], { name: entityName, months })}
          </Typography>
        </Box>
      </GRDialogTitle>
      <DialogContent dividers sx={{ p: 0, maxHeight: '60vh' }}>
        {isLoading ? (
          <Stack spacing={1} sx={{ p: 2 }} aria-busy>
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} variant="rounded" height={GAME_ICON_SIZE} />
            ))}
          </Stack>
        ) : sortedGames.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t(EMPTY_COPY[kind])}
          </Typography>
        ) : (
          <Stack component="ul" divider={<Divider component="li" aria-hidden />} sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {sortedGames.map((game) => {
              const link = calendarLinkFor(
                game,
                eventTypeId,
                game.firstBlueprintId ? detailByBlueprintId.get(game.firstBlueprintId) : undefined,
                kind !== 'blueprint'
              )
              const opensEvent = link.state !== 'calendar'
              const locked = Boolean(game.locked)
              return (
                <Stack key={game.gameId} component="li" direction="row" alignItems="center" spacing={GAME_ICON_GAP} sx={{ px: 2, py: 1 }}>
                  <NewAdopterGameIcon
                    game={game}
                    size={GAME_ICON_SIZE}
                    decorative
                    sx={{ width: GAME_ICON_SIZE, height: GAME_ICON_SIZE, borderRadius: `${GAME_ICON_RADIUS}px`, fontWeight: 700 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {game.name}
                      </Typography>
                      {locked ? <LockedGameIndicator /> : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t('market-analytics:new_adopters_modal_first_event', {
                        date: formatDate(new Date(game.firstEvent), { language: userLanguage, year: 'numeric', month: 'short', day: 'numeric' }),
                      })}
                    </Typography>
                  </Box>
                  {locked ? (
                    <GRTooltip content={t('live-events:locked_game_tooltip')} styles={{ display: 'inline-flex' }}>
                      <IconButton
                        size="small"
                        aria-label={`${game.name} – ${t('live-events:locked_game_tooltip')}`}
                        onClick={(event) => openLockedGameDialog(event, { name: game.name, iconUrl: game.iconUrl, artist: game.artist })}
                        sx={{ color: 'text.disabled' }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </GRTooltip>
                  ) : (
                    <GRTooltip
                      content={t(opensEvent ? 'market-analytics:open_event_in_calendar' : 'market-analytics:open_in_live_events_calendar')}
                      styles={{ display: 'inline-flex' }}
                    >
                      {link.state === 'pending' ? (
                        // Held disabled until the event resolves, so an early click can't land on the bare calendar.
                        <span>
                          <IconButton disabled size="small" aria-label={t('market-analytics:new_adopters_modal_open_event_aria', { game: game.name })}>
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </span>
                      ) : (
                        <IconButton
                          component={RouterLink}
                          to={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label={t(
                            opensEvent ? 'market-analytics:new_adopters_modal_open_event_aria' : 'market-analytics:new_adopters_modal_open_calendar_aria',
                            { game: game.name }
                          )}
                          sx={{ color: 'primary.main' }}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      )}
                    </GRTooltip>
                  )}
                </Stack>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      {lockedGameDialog}
    </GRModal>
  )
}
